import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ShieldAlert, X, FileText, Eye, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

import { api, API } from "@/lib/api";
import { supportCode } from "@/lib/supportCode";
import SupplierSection from "@/pages/invoice-review/SupplierSection";
import InvoiceDataSection from "@/pages/invoice-review/InvoiceDataSection";
import LineItemsSection from "@/pages/invoice-review/LineItemsSection";
import TotalsSection from "@/pages/invoice-review/TotalsSection";

/**
 * Admin-side invoice edit modal.
 *
 * Reuses the SAME section components used in the user's `InvoiceReview` page
 * (SupplierSection, InvoiceDataSection, LineItemsSection, TotalsSection),
 * so the admin sees and edits the exact same fields the user does.
 *
 * On top of that, exposes ADMIN-ONLY overrides (status/sdi_status/notes and
 * a manual `autofattura_number` override) that would otherwise be locked
 * after SDI transmission.
 *
 * The PATCH endpoint (`/admin/invoices/{id}`) auto-regenerates the XML on
 * save if the invoice already has an `xml_path`, so downloads always reflect
 * the latest admin edits.
 */

const EMPTY_EXTRACTED = {
  supplier_name: "", supplier_vat_id: "", supplier_address: "", supplier_city: "",
  supplier_zip: "", supplier_country: "", invoice_number: "", invoice_date: "",
  data_ricezione: "", data_autofattura: "", autofattura_number: "",
  currency: "EUR", total_amount: 0, total_amount_eur: 0,
  imponibile: 0, imposta: 0, aliquota_iva: 22, natura: "",
  service_description: "", is_service: true, is_eu_supplier: false,
  original_currency: "", fx_rate: 1, fx_date: "", fx_source: "",
  confidence: 0, line_items: [],
};

const EMPTY_LINE = {
  descrizione: "", prezzo_totale: 0, quantita: 1, prezzo_unitario: 0, aliquota_iva: 22, natura: "",
};

const genLineId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function AdminInvoiceEditModal({ open, invoice, onClose, onSaved }) {
  const [extracted, setExtracted] = useState(EMPTY_EXTRACTED);
  const [docType, setDocType] = useState("TD17");
  const [status, setStatus] = useState("");
  const [sdiStatus, setSdiStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshingFx, setRefreshingFx] = useState(false);

  // Populate local state whenever a new invoice is opened.
  // Note: EMPTY_EXTRACTED / EMPTY_LINE / genLineId are module-level constants
  // (stable references across renders), so it is safe to omit them from the
  // dependency array.
  useEffect(() => {
    if (!open || !invoice) return;
    const merged = { ...EMPTY_EXTRACTED, ...(invoice.extracted || {}) };
    merged.line_items = (merged.line_items || []).map((li) => ({
      ...EMPTY_LINE,
      ...li,
      id: li?.id || genLineId(),
    }));
    setExtracted(merged);
    setDocType(invoice.document_type || "TD17");
    setStatus(invoice.status || "");
    setSdiStatus(invoice.sdi_status || "");
    setNotes(invoice.notes || "");
  }, [open, invoice]);

  // Auto-sync (line items → imponibile → imposta → totale) — mirror of
  // the user-side InvoiceReview page so admin sees consistent totals.
  useEffect(() => {
    const items = extracted.line_items || [];
    if (items.length === 0) return;
    const sum = items.reduce((s, li) => s + (parseFloat(li.prezzo_totale) || 0), 0);
    const newImp = +sum.toFixed(2);
    setExtracted((p) => (p.imponibile === newImp ? p : { ...p, imponibile: newImp }));
  }, [extracted.line_items]);

  useEffect(() => {
    const imp = parseFloat(extracted.imponibile) || 0;
    const al = parseFloat(extracted.aliquota_iva) || 0;
    const newImposta = +(imp * al / 100).toFixed(2);
    setExtracted((p) => (p.imposta === newImposta ? p : { ...p, imposta: newImposta }));
  }, [extracted.imponibile, extracted.aliquota_iva]);

  useEffect(() => {
    const imp = parseFloat(extracted.imponibile) || 0;
    const imposta = parseFloat(extracted.imposta) || 0;
    const newTotal = +(imp + imposta).toFixed(2);
    setExtracted((p) => {
      if (p.total_amount === newTotal && p.total_amount_eur === newTotal) return p;
      return { ...p, total_amount: newTotal, total_amount_eur: newTotal };
    });
  }, [extracted.imponibile, extracted.imposta]);

  const upd = (k) => (e) => {
    const raw = e.target?.value ?? e;
    let val;
    if (e.target?.type === "number") val = parseFloat(raw || 0);
    else if (k === "aliquota_iva") val = parseFloat(raw || 0);
    else val = raw;
    setExtracted((p) => ({ ...p, [k]: val }));
  };

  const updateLine = (idx, key, value) => {
    setExtracted((p) => {
      const items = [...(p.line_items || [])];
      const next = { ...items[idx], [key]: value };
      if (key === "prezzo_totale") next.prezzo_unitario = value;
      items[idx] = next;
      return { ...p, line_items: items };
    });
  };
  const addLine = () =>
    setExtracted((p) => ({
      ...p,
      line_items: [
        ...(p.line_items || []),
        { ...EMPTY_LINE, id: genLineId(), aliquota_iva: p.aliquota_iva || 22 },
      ],
    }));
  const removeLine = (idx) =>
    setExtracted((p) => ({ ...p, line_items: (p.line_items || []).filter((_, i) => i !== idx) }));

  const sommaRighe = useMemo(
    () => (extracted.line_items || []).reduce((s, li) => s + (parseFloat(li.prezzo_totale) || 0), 0),
    [extracted.line_items]
  );

  // FX refresh triggers the admin-scoped endpoint that recomputes rates.
  // Uses the same public `/invoices/{id}/refresh-fx` endpoint via an
  // admin-friendly variant, if the invoice belongs to a user we can only
  // preview: refuse gracefully instead of hitting a 403.
  const refreshFx = useCallback(async () => {
    if (!invoice?.id) return;
    setRefreshingFx(true);
    try {
      const { data } = await api.post(`/admin/invoices/${invoice.id}/refresh-fx`);
      toast.success(`Tasso aggiornato: ${data.original_currency || extracted.original_currency}→EUR ${(data.fx_rate || 1).toFixed(4)}`);
      // Re-apply the fresh values to local state so the user sees them.
      setExtracted((p) => ({
        ...p,
        fx_rate: data.fx_rate ?? p.fx_rate,
        fx_date: data.fx_date ?? p.fx_date,
        fx_source: data.fx_source ?? p.fx_source,
        imponibile: data.imponibile ?? p.imponibile,
        imposta: data.imposta ?? p.imposta,
        total_amount: data.total_amount ?? p.total_amount,
        total_amount_eur: data.total_amount_eur ?? p.total_amount_eur,
        line_items: data.line_items ?? p.line_items,
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento tasso");
    } finally {
      setRefreshingFx(false);
    }
  }, [invoice?.id, extracted.original_currency]);

  const openXmlNewTab = () => {
    const token = localStorage.getItem("auth_token");
    fetch(`${API}/invoices/${invoice.id}/xml`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => toast.error("Impossibile aprire l'XML"));
  };

  const openPdfNewTab = () => {
    const token = localStorage.getItem("auth_token");
    fetch(`${API}/invoices/${invoice.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      })
      .catch(() => toast.error("Impossibile aprire il PDF"));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!invoice) return;
    setSaving(true);
    try {
      const payload = {
        extracted,
        document_type: docType,
        // Admin can override AF number manually (locked-out post-SDI otherwise)
        autofattura_number: extracted.autofattura_number || null,
        status: status || null,
        sdi_status: sdiStatus || null,
        notes: notes || null,
      };
      await api.patch(`/admin/invoices/${invoice.id}`, payload);
      toast.success(invoice.xml_path
        ? "Fattura aggiornata · XML rigenerato"
        : "Fattura aggiornata"
      );
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !invoice) return null;

  const ownerEmail = invoice.user?.email || "—";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 grid place-items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-invoice-edit-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FAF9F6] rounded-2xl shadow-2xl max-w-6xl w-full my-4"
      >
        {/* Editorial header — same tone as InvoiceReview */}
        <div className="sticky top-0 bg-white border-b border-[#E8E4D9] px-6 py-4 flex items-center justify-between gap-3 z-10 rounded-t-2xl">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-0.5">
              Admin · Modifica autofattura
            </div>
            <h3 className="font-display font-extrabold text-2xl tracking-tight truncate leading-none text-[#0A0A0A]" data-testid="admin-invoice-edit-title">
              {extracted.autofattura_number ? (
                <>
                  <span className="font-serif-accent text-[#1D4ED8]">#</span>
                  {extracted.autofattura_number}
                </>
              ) : (
                <span className="text-[#737373] italic font-normal text-lg">
                  Numero non assegnato
                </span>
              )}
            </h3>
            <div className="flex items-center gap-2 flex-wrap mt-1.5 text-xs text-[#737373]">
              <span
                className="font-mono font-bold text-[11px] text-[#1D4ED8] px-2 py-0.5 rounded bg-blue-50 border border-blue-200"
                title={invoice.id}
                data-testid="admin-invoice-edit-support-code"
              >
                {supportCode(invoice.id)}
              </span>
              <span className="uppercase tracking-widest text-[10px] font-bold text-[#0A0A0A]">
                {invoice.status}
              </span>
              {invoice.sdi_status && (
                <span className="uppercase tracking-widest text-[10px] font-bold text-emerald-700">
                  SDI · {invoice.sdi_status}
                </span>
              )}
              <span className="text-[#A3A3A3]">·</span>
              <span className="truncate">{ownerEmail}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {invoice.file_path && (
              <button
                type="button"
                onClick={openPdfNewTab}
                data-testid="admin-invoice-edit-open-pdf"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#E8E4D9] bg-white text-xs hover:bg-[#F5F2EA] transition-colors"
              >
                <FileText className="w-3.5 h-3.5" /> PDF
              </button>
            )}
            {invoice.xml_path && (
              <button
                type="button"
                onClick={openXmlNewTab}
                data-testid="admin-invoice-edit-open-xml"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#E8E4D9] bg-white text-xs hover:bg-[#F5F2EA] transition-colors"
              >
                <Eye className="w-3.5 h-3.5" /> XML
              </button>
            )}
            <Link
              to={`/app/invoices/${invoice.id}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-invoice-edit-open-user-view"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#E8E4D9] bg-white text-xs hover:bg-[#F5F2EA] transition-colors"
              title="Apri la stessa vista fattura in una nuova scheda (come la vede l'utente)"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Vista utente
            </Link>
            <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#F5F2EA] rounded-md" data-testid="admin-invoice-edit-close">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-4">
          {/* Admin-only overrides */}
          <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-4" data-testid="admin-invoice-overrides">
            <div className="flex items-center gap-2 mb-3">
              <ShieldAlert className="w-4 h-4 text-amber-700" />
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-800">
                Override admin · usa con giudizio
              </div>
            </div>
            <p className="text-xs text-amber-800 mb-3 leading-snug">
              Questi campi sono normalmente <strong>bloccati</strong> per l&apos;utente dopo la trasmissione SDI.
              L&apos;admin può forzarli. Il salvataggio <strong>rigenera automaticamente l&apos;XML</strong> se già presente.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] uppercase text-zinc-600 font-semibold mb-1">Status fattura</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  data-testid="admin-inv-override-status"
                  className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm"
                >
                  <option value="">—</option>
                  <option value="uploaded">Caricata</option>
                  <option value="extracting">In estrazione</option>
                  <option value="extracted">Estratta</option>
                  <option value="reviewed">Revisionata</option>
                  <option value="xml_generated">XML pronto</option>
                  <option value="error">Errore</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase text-zinc-600 font-semibold mb-1">Stato SDI</label>
                <select
                  value={sdiStatus}
                  onChange={(e) => setSdiStatus(e.target.value)}
                  data-testid="admin-inv-override-sdi"
                  className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm"
                >
                  <option value="">—</option>
                  <option value="RC">RC · Consegnata</option>
                  <option value="NS">NS · Scartata</option>
                  <option value="MC">MC · Mancata consegna</option>
                  <option value="DT">DT · Termini decorsi</option>
                  <option value="NE">NE · Esito notificato</option>
                  <option value="EC">EC · Esito committente</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase text-zinc-600 font-semibold mb-1">Note admin</label>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="admin-inv-override-notes"
                  placeholder="Motivo intervento…"
                  className="w-full h-10 px-3 rounded-md border border-[#E8E4D9] bg-white text-sm"
                />
              </div>
            </div>
          </div>

          {/* Same 2-column layout as InvoiceReview */}
          <fieldset className="border-0 p-0 m-0" data-testid="admin-invoice-fieldset">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <SupplierSection
                extracted={extracted}
                upd={upd}
                suppliers={[]}
                applySupplier={() => {}}
                hidePicker
              />
              <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4D9] p-5 sm:p-6">
                <InvoiceDataSection
                  extracted={extracted}
                  upd={upd}
                  docType={docType}
                  setDocType={setDocType}
                  refreshFx={refreshFx}
                  refreshingFx={refreshingFx}
                />
                <LineItemsSection
                  extracted={extracted}
                  updateLine={updateLine}
                  addLine={addLine}
                  removeLine={removeLine}
                  sommaRighe={sommaRighe}
                />
              </div>
            </div>
            <TotalsSection extracted={extracted} upd={upd} />
          </fieldset>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 bg-white border-t border-[#E8E4D9] px-6 py-4 flex items-center justify-end gap-2 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-invoice-edit-cancel"
            className="px-4 py-2 rounded-md border border-[#E8E4D9] bg-white text-sm hover:bg-[#F5F2EA]"
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="admin-invoice-edit-save"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-indigo-600 text-white text-sm font-bold uppercase tracking-wide hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salva modifiche
          </button>
        </div>
      </form>
    </div>
  );
}
